import { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { selectFilters } from "@/features/bughurd/boardSelector";
import {
  setSelectedStatuses,
  setSelectedPriorities,
  setSelectedProjects,
  setDueDateFilter,
  setStartDateFilter,
  setUpdatedDateFilter,
  clearAllFilters,
  setIsRecurring,
  setSelectedTags,
  setSelectedAvatars,
  setSelectedCreatedBy,
} from "@/features/bughurd/slice";
import { getDateRange } from "@/pages/Board/utils";

interface Filter {
  id: string;
  type:
    | "status"
    | "priority"
    | "due_date"
    | "start_date"
    | "updated_date"
    | "project"
    | "recurring"
    | "tags"
    | "assignee"
    | "created_by";
  value: string | string[] | boolean;
  startDate?: string;
  endDate?: string;
}

interface SavedFilter {
  id: string;
  name: string;
  filters: Filter[];
  createdAt: string;
  updatedAt: string;
}

export const useFilterState = (isMyTaskView: boolean) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);

  const [filterComponents, setFilterComponents] = useState<Filter[]>([]);
  const [currentLoadedFilterId, setCurrentLoadedFilterId] = useState<
    string | null
  >(null);

  const {
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedTags,
    selectedAvatars,
    selectedCreatedBy,
  } = filters;

  // Initialize filters based on Redux state
  useEffect(() => {
    const activeFilters: Filter[] = [];

    if (selectedStatuses?.length > 0) {
      activeFilters.push({
        id: "status",
        type: "status",
        value: selectedStatuses,
      });
    }

    if (selectedPriorities?.length > 0) {
      activeFilters.push({
        id: "priority",
        type: "priority",
        value: selectedPriorities,
      });
    }

    if (isMyTaskView && selectedProjects?.length > 0) {
      activeFilters.push({
        id: "project",
        type: "project",
        value: selectedProjects,
      });
    }

    if (selectedTags?.length > 0) {
      activeFilters.push({
        id: "tags",
        type: "tags",
        value: selectedTags,
      });
    }

    if (selectedAvatars?.length > 0) {
      activeFilters.push({
        id: "assignee",
        type: "assignee",
        value: selectedAvatars,
      });
    }

    if (selectedCreatedBy?.length > 0) {
      activeFilters.push({
        id: "created_by",
        type: "created_by",
        value: selectedCreatedBy,
      });
    }

    if (selectedDueDate) {
      activeFilters.push({
        id: "due_date",
        type: "due_date",
        value: selectedDueDate,
        startDate: dueDateStart,
        endDate: dueDateEnd,
      });
    }

    if (selectedStartDate) {
      activeFilters.push({
        id: "start_date",
        type: "start_date",
        value: selectedStartDate,
        startDate: startDateStart,
        endDate: startDateEnd,
      });
    }

    if (selectedUpdatedDate) {
      activeFilters.push({
        id: "updated_date",
        type: "updated_date",
        value: selectedUpdatedDate,
        startDate: updatedAtStart,
        endDate: updatedAtEnd,
      });
    }
    // If isRecurring is true, add a filter for recurring tasks
    if (isRecurring) {
      activeFilters.push({
        id: "recurring",
        type: "recurring",
        value: isRecurring,
      });
    }

    setFilterComponents(activeFilters);
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isMyTaskView,
    isRecurring,
    selectedTags,
    selectedAvatars,
    selectedCreatedBy,
  ]);

  // Detect when filters are cleared from other components
  useEffect(() => {
    const hasAnyActiveFilter =
      selectedStatuses?.length > 0 ||
      selectedPriorities?.length > 0 ||
      (isMyTaskView && selectedProjects?.length > 0) ||
      selectedDueDate ||
      selectedStartDate ||
      selectedUpdatedDate ||
      selectedTags?.length > 0 ||
      selectedAvatars?.length > 0 ||
      selectedCreatedBy?.length > 0;

    if (!hasAnyActiveFilter && currentLoadedFilterId) {
      setCurrentLoadedFilterId(null);
    }
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    selectedDueDate,
    selectedStartDate,
    selectedUpdatedDate,
    currentLoadedFilterId,
    isMyTaskView,
    selectedTags,
    selectedAvatars,
    selectedCreatedBy,
  ]);

  // Add a new filter
  const addFilter = (filterType: Filter["type"]) => {
    const newFilter: Filter = {
      id: `${filterType}-${Date.now()}`,
      type: filterType,
      value:
        filterType === "status" ||
        filterType === "priority" ||
        filterType === "project" ||
        filterType === "tags" ||
        filterType === "assignee"
          ? []
          : filterType === "recurring"
          ? false
          : "",
    };
    setFilterComponents([...filterComponents, newFilter]);
  };

  // Update filter value
  const updateFilterValue = (
    filterId: string,
    value: string | string[] | boolean
  ) => {
    let startDate = "";
    let endDate = "";

    const filter = filterComponents.find((f) => f.id === filterId);

    if (
      (filter?.type === "due_date" ||
        filter?.type === "start_date" ||
        filter?.type === "updated_date") &&
      typeof value === "string" &&
      value !== "date_range"
    ) {
      if (value === "no_date") {
        startDate = "no-date";
        endDate = "no-date";
      } else {
        const dateRange = getDateRange(value);
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      }
    }

    setFilterComponents(
      filterComponents.map((filter) =>
        filter.id === filterId
          ? {
              ...filter,
              value,
              startDate: value === "date_range" ? "" : startDate,
              endDate: value === "date_range" ? "" : endDate,
            }
          : filter
      )
    );

    // Update Redux state
    if (filter?.type === "status") {
      dispatch(setSelectedStatuses(value as string[]));
    } else if (filter?.type === "priority") {
      dispatch(setSelectedPriorities(value as string[]));
    } else if (filter?.type === "project") {
      dispatch(setSelectedProjects(value as string[]));
    } else if (filter?.type === "due_date") {
      dispatch(
        setDueDateFilter({
          selectedDueDate: value as string,
          dueDateStart: value === "date_range" ? "" : startDate,
          dueDateEnd: value === "date_range" ? "" : endDate,
        })
      );
    } else if (filter?.type === "start_date") {
      dispatch(
        setStartDateFilter({
          selectedStartDate: value as string,
          startDateStart: value === "date_range" ? "" : startDate,
          startDateEnd: value === "date_range" ? "" : endDate,
        })
      );
    } else if (filter?.type === "updated_date") {
      dispatch(
        setUpdatedDateFilter({
          selectedUpdatedDate: value as string,
          updatedAtStart: value === "date_range" ? "" : startDate,
          updatedAtEnd: value === "date_range" ? "" : endDate,
        })
      );
    } else if (filter?.type === "recurring") {
      dispatch(setIsRecurring(value as boolean));
    } else if (filter?.type === "tags") {
      dispatch(setSelectedTags(value as string[]));
    } else if (filter?.type === "assignee") {
      dispatch(setSelectedAvatars(value as string[]));
    } else if (filter?.type === "created_by") {
      dispatch(setSelectedCreatedBy(value as string[]));
    }
  };

  // Update filter date
  const updateFilterDate = (
    filterId: string,
    dateType: "start" | "end",
    date: string
  ) => {
    setFilterComponents(
      filterComponents.map((filter) =>
        filter.id === filterId
          ? {
              ...filter,
              [dateType === "start" ? "startDate" : "endDate"]: date,
            }
          : filter
      )
    );

    const updatedFilter = filterComponents.find((f) => f.id === filterId);
    if (updatedFilter) {
      const startDate =
        dateType === "start" ? date : updatedFilter.startDate || "";
      const endDate = dateType === "end" ? date : updatedFilter.endDate || "";

      if (updatedFilter.type === "due_date") {
        dispatch(
          setDueDateFilter({
            selectedDueDate: updatedFilter.value as string,
            dueDateStart: startDate,
            dueDateEnd: endDate,
          })
        );
      } else if (updatedFilter.type === "start_date") {
        dispatch(
          setStartDateFilter({
            selectedStartDate: updatedFilter.value as string,
            startDateStart: startDate,
            startDateEnd: endDate,
          })
        );
      } else if (updatedFilter.type === "updated_date") {
        dispatch(
          setUpdatedDateFilter({
            selectedUpdatedDate: updatedFilter.value as string,
            updatedAtStart: startDate,
            updatedAtEnd: endDate,
          })
        );
      }
    }
  };

  // Remove a filter
  const removeFilter = (filterId: string) => {
    const filterToRemove = filterComponents.find((f) => f.id === filterId);

    if (filterToRemove?.type === "status") {
      dispatch(setSelectedStatuses([]));
    } else if (filterToRemove?.type === "priority") {
      dispatch(setSelectedPriorities([]));
    } else if (filterToRemove?.type === "project") {
      dispatch(setSelectedProjects([]));
    } else if (filterToRemove?.type === "due_date") {
      dispatch(
        setDueDateFilter({
          selectedDueDate: "",
          dueDateStart: "",
          dueDateEnd: "",
        })
      );
    } else if (filterToRemove?.type === "start_date") {
      dispatch(
        setStartDateFilter({
          selectedStartDate: "",
          startDateStart: "",
          startDateEnd: "",
        })
      );
    } else if (filterToRemove?.type === "updated_date") {
      dispatch(
        setUpdatedDateFilter({
          selectedUpdatedDate: "",
          updatedAtStart: "",
          updatedAtEnd: "",
        })
      );
    } else if (filterToRemove?.type === "recurring") {
      dispatch(setIsRecurring(false));
    } else if (filterToRemove?.type === "tags") {
      dispatch(setSelectedTags([]));
    } else if (filterToRemove?.type === "assignee") {
      dispatch(setSelectedAvatars([]));
    } else if (filterToRemove?.type === "created_by") {
      dispatch(setSelectedCreatedBy([]));
    }

    setFilterComponents(
      filterComponents.filter((filter) => filter.id !== filterId)
    );
  };

  // Clear all filters
  const clearAll = () => {
    setFilterComponents([]);
    setCurrentLoadedFilterId(null);
    dispatch(clearAllFilters());
  };

  // Apply filters to Redux state
  const applyFiltersToRedux = (filters: Filter[]) => {
    dispatch(clearAllFilters());

    filters.forEach((filter) => {
      if (filter.type === "status" && Array.isArray(filter.value)) {
        dispatch(setSelectedStatuses(filter.value));
      } else if (filter.type === "priority" && Array.isArray(filter.value)) {
        dispatch(setSelectedPriorities(filter.value));
      } else if (filter.type === "project" && Array.isArray(filter.value)) {
        dispatch(setSelectedProjects(filter.value));
      } else if (
        filter.type === "due_date" &&
        typeof filter.value === "string"
      ) {
        dispatch(
          setDueDateFilter({
            selectedDueDate: filter.value,
            dueDateStart: filter.startDate || "",
            dueDateEnd: filter.endDate || "",
          })
        );
      } else if (
        filter.type === "start_date" &&
        typeof filter.value === "string"
      ) {
        dispatch(
          setStartDateFilter({
            selectedStartDate: filter.value,
            startDateStart: filter.startDate || "",
            startDateEnd: filter.endDate || "",
          })
        );
      } else if (
        filter.type === "updated_date" &&
        typeof filter.value === "string"
      ) {
        dispatch(
          setUpdatedDateFilter({
            selectedUpdatedDate: filter.value,
            updatedAtStart: filter.startDate || "",
            updatedAtEnd: filter.endDate || "",
          })
        );
      } else if (
        filter.type === "recurring" &&
        typeof filter.value === "boolean"
      ) {
        dispatch(setIsRecurring(filter.value));
      } else if (filter.type === "tags" && Array.isArray(filter.value)) {
        dispatch(setSelectedTags(filter.value));
      } else if (filter.type === "assignee" && Array.isArray(filter.value)) {
        dispatch(setSelectedAvatars(filter.value));
      } else if (filter.type === "created_by" && Array.isArray(filter.value)) {
        dispatch(setSelectedCreatedBy(filter.value));
      }
    });
  };

  // Load a saved filter
  const loadSavedFilter = (savedFilter: SavedFilter) => {
    applyFiltersToRedux(savedFilter.filters);
    setTimeout(() => {
      setCurrentLoadedFilterId(savedFilter.id);
    }, 100);
  };

  const hasActiveFilters = filterComponents.some((f) =>
    Array.isArray(f.value) ? f.value.length > 0 : f.value
  );

  return {
    filterComponents,
    currentLoadedFilterId,
    hasActiveFilters,
    addFilter,
    updateFilterValue,
    updateFilterDate,
    removeFilter,
    clearAll,
    loadSavedFilter,
    setCurrentLoadedFilterId,
  };
};
